import React, { FC, useRef, useMemo, useCallback } from 'react';
import nextId from 'react-id-generator';
import {
    ProductThumbnail,
    OnSelectProductHandler,
} from '@app/react/uikit';
import { PartsProduct, ProductWithCategory } from '@app/react/const/type';
import { Link, Element } from 'react-scroll';

const PREFIX = 'tab-product-categorylist';

const ScrollableContainer: FC = ({ children }) => (
    <Element
        className={`${PREFIX}--scrollable-container`}
        id="containerElement"
        name="containerElement"
    >
        {children}
    </Element>
);

const Section: FC<{ index: number }> = ({ children, index }) => (
    <Element
        className={`${PREFIX}--section`}
        id={`section-${index}`}
        name={`section-${index}`}
    >
        {children}
    </Element>
);

type SectionItemProps = { categoryName: string, length?: number };
const SectionItem: FC<SectionItemProps> = ({ children, categoryName, length }) => (
    <>
        <p className="section-category-label">
            {categoryName}
            {length !== undefined && (`(${length})`)}
        </p>
        <ul className="product-container">
            {children}
        </ul>
    </>
);

const SectionLink: FC<{ index: number, onSetActive: (to: string) => void }> = ({
    children,
    index,
    onSetActive,
}) => (
    <Link
        className={`${PREFIX}--header-link-item`}
        activeClass={`${PREFIX}--header-active`}
        to={`section-${index}`}
        containerId="containerElement"
        spy
        smooth
        duration={100}
        onSetActive={onSetActive}
        offset={-40}
    >
        {children}
    </Link>
);

interface TabProductCategoryListProps {
    noProduct: PartsProduct;
    viewData: ProductWithCategory;
    selectedProduct: PartsProduct | null;
    onSelectProduct: OnSelectProductHandler;
    allDisabled?: boolean;
}

export const TabProductCategoryList: FC<TabProductCategoryListProps> = (props) => {
    const {
        noProduct,
        viewData,
        selectedProduct,
        onSelectProduct,
        allDisabled = false,
    } = props;

    const headersRef = useRef<{ [key in number]: HTMLLIElement }>({});
    const htmlId = useMemo(() => nextId('tab-categorylist'), []);

    const activeHeader = useCallback((index: number) => {
        headersRef.current[index]?.scrollIntoView({ inline: 'center' });
    }, [headersRef]);

    return (
        <>
            <ul className={`${PREFIX}--header`}>
                <li
                    className={`${PREFIX}--header-link-item-wrapper`}
                    ref={(ele: HTMLLIElement) => { headersRef.current[0] = ele; }}
                >
                    <SectionLink index={0} onSetActive={() => activeHeader(0)}>
                        加工なし
                    </SectionLink>
                </li>

                {viewData.map((category, index) => {
                    const length = category.subCategoryData.reduce(
                        (acc, subcategorys) => acc + subcategorys.products.length,
                        0,
                    );
                    return (
                        <li
                            key={category.categoryId}
                            className={`${PREFIX}--header-link-item-wrapper`}
                            ref={(ele: HTMLLIElement) => { headersRef.current[index + 1] = ele; }}
                        >
                            <SectionLink
                                index={index + 1}
                                onSetActive={() => activeHeader(index + 1)}
                            >
                                {category.categoryName}
                                (
                                {length}
                                )
                            </SectionLink>
                        </li>
                    );
                })}
            </ul>
            <ScrollableContainer>
                <Section index={0}>
                    <SectionItem categoryName="加工なし">
                        <ProductThumbnail
                            product={noProduct}
                            onClick={onSelectProduct}
                            checked={noProduct.id === selectedProduct?.id}
                            groupName={htmlId}
                        />
                    </SectionItem>
                </Section>

                {viewData.map((category, index) => {
                    const length = category.subCategoryData.reduce(
                        (acc, subcategorys) => acc + subcategorys.products.length,
                        0,
                    );
                    return (
                        <Section key={category.categoryId} index={index + 1}>
                            <SectionItem categoryName={category.categoryName} length={length}>
                                {category.subCategoryData.map(
                                    (subcategorys) => subcategorys.products.map(
                                        (product) => (
                                            <ProductThumbnail
                                                key={product.id}
                                                product={product}
                                                onClick={onSelectProduct}
                                                checked={product.id === selectedProduct?.id}
                                                groupName={htmlId}
                                                disabled={allDisabled}
                                            />
                                        ),
                                    ),
                                )}
                            </SectionItem>
                        </Section>
                    );
                })}
            </ScrollableContainer>
        </>
    );
};
